import { ChallengeService } from '@duck/api-definitions'
import { TranslationKey, useTar } from '@/modules'
import { cn } from '@duck/ui'
import { WeaponIcon } from '@components/WeaponIcon'

const WEAPON_LABEL_KEY: Record<ChallengeService.TaskWeaponsOfChoiceEnum, TranslationKey> = {
  [ChallengeService.TaskWeaponsOfChoiceEnum.Sql]: 'common.sql',
  [ChallengeService.TaskWeaponsOfChoiceEnum.Python]: 'common.python',
}

/**
 * Resolves the weapon a finished submission was actually solved with. Legacy
 * submissions predate persisting `chosenWeapon`, so fall back to the task's
 * first allowed weapon, then SQL — matching how the rest of the app treats a
 * missing weapon. Use in results/submission contexts (never browsing) so the
 * pills collapse to the single used weapon instead of every allowed one.
 */
export const resolveUsedWeapon = (
  chosenWeapon: ChallengeService.TaskWeaponsOfChoiceEnum | undefined,
  allowedWeapons: ChallengeService.TaskWeaponsOfChoiceEnum[] | undefined
): ChallengeService.TaskWeaponsOfChoiceEnum =>
  chosenWeapon ?? allowedWeapons?.[0] ?? ChallengeService.TaskWeaponsOfChoiceEnum.Sql

interface AllowedWeaponsPillsProps {
  allowedWeapons: ChallengeService.TaskWeaponsOfChoiceEnum[] | undefined
  chosenWeapon?: ChallengeService.TaskWeaponsOfChoiceEnum
  className?: string
  /** Extra classes applied to each individual weapon pill. */
  pillClassName?: string
}

export const AllowedWeaponsPills = ({
  allowedWeapons,
  chosenWeapon,
  className,
  pillClassName,
}: AllowedWeaponsPillsProps) => {
  const { t } = useTar()
  const allWeapons = allowedWeapons && allowedWeapons.length > 0
    ? allowedWeapons
    : [ChallengeService.TaskWeaponsOfChoiceEnum.Sql]
  // Once a weapon has been chosen for a submission, show only that one —
  // the other allowed options are no longer relevant. Browsing contexts pass
  // no chosenWeapon and keep showing every allowed weapon.
  const showOnlyChosen = chosenWeapon != null && allWeapons.includes(chosenWeapon)
  const weapons = showOnlyChosen ? [chosenWeapon] : allWeapons

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      {weapons.map((w) => (
        <span
          key={w}
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full border uppercase tracking-wide',
            'text-accent border-accent/40',
            pillClassName
          )}
        >
          <WeaponIcon weapon={w} className="h-3.5 w-3.5 shrink-0" />
          {t(WEAPON_LABEL_KEY[w])}
        </span>
      ))}
    </div>
  )
}
